import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import CallButton from './CallButton';
import CallHistoryModal from './CallHistoryModal';
import WhatsAppCustomerPanel from './WhatsAppCustomerPanel';
import { CloseIcon, CopyIcon, PhoneIcon, WhatsAppIcon } from './Icons';
import { getCustomerNumber, getKnownAgents, groupByCustomer, normalizeNumber } from './callUtils';
import { EMAIL_CATEGORIES, EMAIL_PRIORITIES, isEmailKey } from './emailUtils';
import { StatusPill } from './EmailThread';

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked: nothing to do.
    }
  };
  return (
    <button type="button" className="ew-icon-btn ew-icon-btn-xs" onClick={copy} title={copied ? 'Copied' : `Copy ${label}`} aria-label={`Copy ${label}`}>
      {copied ? '✓' : <CopyIcon width={12} height={12} />}
    </button>
  );
}

// Links an email-only conversation to an existing customer profile by phone
// number (POST /api/email/conversations/:key/link). The server only fills in
// an empty profile email, never overwrites one.
function LinkCustomerForm({ conversationKey, onLinked }) {
  const { authFetch } = useAuth();
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    const number = normalizeNumber(phone.trim());
    if (!number) return;
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`/api/email/conversations/${encodeURIComponent(conversationKey)}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerNumber: number }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not link this customer');
      onLinked?.(data.movedTo);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ew-link-form" onSubmit={submit}>
      <p className="ew-muted">
        This sender isn't linked to a customer profile, so there are no orders or calls to show. If you know their
        phone number, link it:
      </p>
      <div className="ew-link-row">
        <input
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="Customer phone number"
          aria-label="Customer phone number"
          disabled={saving}
        />
        <button type="submit" className="btn btn-secondary btn-small" disabled={saving || !phone.trim()}>
          {saving ? 'Linking…' : 'Link'}
        </button>
      </div>
      {error && <div className="ew-field-error">{error}</div>}
    </form>
  );
}

// Right-hand panel: who the customer is, quick actions (copy, call, open
// WhatsApp, call history), this conversation's workflow settings, and, for a
// customer with a phone-based profile, the same orders / calls / follow-ups
// panel the WhatsApp page shows (WhatsAppCustomerPanel, reused as-is).
function EmailCustomerPanel({
  conversationKey,
  conversation,
  calls = [],
  assignees = [],
  onUpdateState,
  onOpenWhatsapp,
  onLinked,
  onClose,
}) {
  const { authFetch } = useAuth();
  const isAddressOnly = isEmailKey(conversationKey);
  const [profile, setProfile] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setProfile(null);
    if (isAddressOnly) return undefined;
    let cancelled = false;
    authFetch(`/api/customers/${encodeURIComponent(conversationKey)}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => !cancelled && setProfile(data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authFetch, conversationKey, isAddressOnly]);

  const normalizedKey = isAddressOnly ? null : normalizeNumber(conversationKey);
  const hasCalls = useMemo(
    () => Boolean(normalizedKey) && calls.some((call) => getCustomerNumber(call) === normalizedKey),
    [calls, normalizedKey]
  );
  const historyGroup = useMemo(
    () => (historyOpen ? groupByCustomer(calls).find((group) => group.customerNumber === normalizedKey) : null),
    [historyOpen, calls, normalizedKey]
  );
  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);

  const change = async (changes) => {
    setSaving(true);
    setError('');
    try {
      await onUpdateState(changes);
    } catch (err) {
      setError(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const email = conversation?.email || (isAddressOnly ? conversationKey : profile?.email) || '';
  const whatsappNumber = profile?.whatsapp_number || (isAddressOnly ? null : conversationKey);
  const resolved = conversation?.status === 'resolved';

  return (
    <aside className="ew-panel" aria-label="Customer details">
      <div className="ew-panel-head">
        <span>Customer</span>
        {onClose && (
          <button type="button" className="ew-icon-btn ew-icon-btn-xs" onClick={onClose} aria-label="Close customer details">
            <CloseIcon width={14} height={14} />
          </button>
        )}
      </div>

      <section className="ew-panel-section">
        <dl className="ew-fields">
          {email && (
            <>
              <dt>Email</dt>
              <dd>
                <span className="ew-field-value" title={email}>
                  {email}
                </span>
                <CopyButton value={email} label="email" />
              </dd>
            </>
          )}
          {!isAddressOnly && (
            <>
              <dt>Phone</dt>
              <dd>
                <span className="ew-field-value">{conversationKey}</span>
                <CopyButton value={conversationKey} label="phone number" />
              </dd>
            </>
          )}
          {profile?.whatsapp_number && profile.whatsapp_number !== conversationKey && (
            <>
              <dt>WhatsApp</dt>
              <dd>
                <span className="ew-field-value">{profile.whatsapp_number}</span>
              </dd>
            </>
          )}
          {(profile?.city || profile?.state) && (
            <>
              <dt>Location</dt>
              <dd>{[profile.city, profile.state].filter(Boolean).join(', ')}</dd>
            </>
          )}
          {profile?.order_number && (
            <>
              <dt>Order #</dt>
              <dd>
                <span className="ew-field-value">{profile.order_number}</span>
                <CopyButton value={profile.order_number} label="order number" />
              </dd>
            </>
          )}
        </dl>
        {!isAddressOnly && (
          <div className="ew-panel-actions">
            {knownAgents.length > 0 && <CallButton agents={knownAgents} customerNumber={conversationKey} onCallTriggered={() => {}} />}
            {onOpenWhatsapp && whatsappNumber && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => onOpenWhatsapp(whatsappNumber)}>
                <WhatsAppIcon width={14} height={14} />
                WhatsApp
              </button>
            )}
            {hasCalls && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setHistoryOpen(true)}>
                <PhoneIcon width={14} height={14} />
                Call history
              </button>
            )}
          </div>
        )}
      </section>

      {conversation && (
        <section className="ew-panel-section">
          <h4 className="ew-panel-title">
            Conversation <StatusPill status={conversation.status} />
          </h4>
          <div className="ew-panel-grid">
            <label>
              <span>Priority</span>
              <select
                value={conversation.priority || 'normal'}
                onChange={(event) => change({ priority: event.target.value })}
                disabled={saving}
              >
                {EMAIL_PRIORITIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Category</span>
              <select
                value={conversation.category || ''}
                onChange={(event) => change({ category: event.target.value || null })}
                disabled={saving}
              >
                <option value="">None</option>
                {EMAIL_CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="ew-panel-grid-wide">
              <span>Assigned to</span>
              <select
                value={conversation.assigned_user_id ?? ''}
                onChange={(event) => change({ assignedUserId: event.target.value ? Number(event.target.value) : null })}
                disabled={saving}
              >
                <option value="">Unassigned</option>
                {assignees.map((assignee) => (
                  <option key={assignee.id} value={assignee.id}>
                    {assignee.display_name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-small ew-resolve-btn"
            onClick={() => change({ resolved: !resolved })}
            disabled={saving}
          >
            {resolved ? 'Reopen conversation' : 'Mark resolved'}
          </button>
          {error && <div className="ew-field-error">{error}</div>}
        </section>
      )}

      {isAddressOnly ? (
        <section className="ew-panel-section">
          <LinkCustomerForm conversationKey={conversationKey} onLinked={onLinked} />
        </section>
      ) : (
        <div className="ew-panel-embedded">
          <WhatsAppCustomerPanel customerNumber={conversationKey} calls={calls} showIdentity={false} />
        </div>
      )}

      {historyOpen && historyGroup && (
        <CallHistoryModal
          group={historyGroup}
          onClose={() => setHistoryOpen(false)}
          agents={knownAgents}
          onCallTriggered={() => {}}
        />
      )}
    </aside>
  );
}

export default EmailCustomerPanel;
